import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import * as D from "@radix-ui/react-dialog";
import { TooltipProvider } from "@dfws/ui";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { MobileNav } from "./MobileNav";
import { CommandPalette } from "../command/CommandPalette";
import { Cheatsheet } from "../command/Cheatsheet";
import { useShortcuts } from "../command/shortcuts";
import { updateSettings, useSettings } from "../settings/store";
import { useApplyTheme } from "../settings/theme";
import { useMediaQuery } from "./useMediaQuery";
import { useSession } from "../lib/queries";
import { api } from "../lib/api";
import { connectRemote } from "../settings/store";
import type { UserSettings } from "@dfws/contracts";
import { useRealtime } from "../lib/realtime";
import { ComposeDock, Toaster } from "../features/compose/ComposeDock";
import { newCompose } from "../features/compose/composeData";
import { ChatDock } from "../features/chat/ChatDock";
import { useOpenChat } from "../features/chat/useOpenChat";
import { usePresenceReporter } from "../features/chat/presence";
import { setNotificationOpener, totalUnread, useChannels, useChatPeople } from "../features/chat/chatData";
import s from "./AppShell.module.css";

export function AppShell() {
  useApplyTheme();
  const session = useSession();
  const signedIn = Boolean(session.data);
  const me = (session.data?.user.email ?? "").toLowerCase();
  useRealtime(signedIn, me);
  usePresenceReporter(signedIn);
  // Loaded up front so badges, names and notifications work before chat is opened.
  const channels = useChannels(signedIn);
  useChatPeople(signedIn);
  const settings = useSettings();
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width: 899px)");
  const openChat = useOpenChat(isPhone);
  useEffect(() => setNotificationOpener(openChat));
  const chatUnread = totalUnread(channels.data?.items);
  useEffect(() => {
    document.title = chatUnread ? `(${chatUnread}) DevFusion Workspace` : "DevFusion Workspace";
  }, [chatUnread]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleSidebar = () => (isPhone ? setDrawerOpen((o) => !o) : updateSettings({ sidebarCollapsed: !settings.sidebarCollapsed }));
  const openPalette = (q = "") => {
    setPaletteQuery(q);
    setPaletteOpen(true);
  };

  useEffect(() => {
    if (session.isSuccess && !session.data) void navigate({ to: "/login" });
  }, [session.isSuccess, session.data, navigate]);

  // Settings follow the account across devices.
  const userId = session.data?.user.id;
  useEffect(() => {
    if (!userId) return;
    void api<{ stored: boolean; settings: UserSettings }>("/settings")
      .then((remote) => connectRemote(remote, (s) => void api("/settings", { method: "PUT", body: s }).catch(() => {})))
      .catch(() => {});
  }, [userId]);

  useShortcuts(
    {
      palette: () => (paletteOpen ? setPaletteOpen(false) : openPalette()),
      help: () => setHelpOpen(true),
      search: () => openPalette(),
      toggleSidebar,
      goInbox: () => navigate({ to: "/mail/$folder", params: { folder: "inbox" } }),
      goDrafts: () => navigate({ to: "/mail/$folder", params: { folder: "drafts" } }),
      goSent: () => navigate({ to: "/mail/$folder", params: { folder: "sent" } }),
      goCalendar: () => navigate({ to: "/calendar" }),
      goContacts: () => navigate({ to: "/contacts" }),
      goSettings: () => navigate({ to: "/settings" }),
      compose: () => void newCompose(),
    },
    settings.shortcutsEnabled,
  );

  if (!signedIn) return <div className={s.booting} aria-busy="true" aria-label="Loading Workspace" />;

  return (
    <TooltipProvider>
      <a href="#main" className={s.skip}>
        Skip to content
      </a>
      <div className={s.shell} data-phone={isPhone || undefined}>
        {!isPhone && <Sidebar collapsed={settings.sidebarCollapsed} />}
        <div className={s.column}>
          <TopBar onToggleSidebar={toggleSidebar} onOpenPalette={openPalette} onOpenHelp={() => setHelpOpen(true)} email={session.data?.user.email ?? ""} showChat={!isPhone} />
          <main id="main" className={s.main} tabIndex={-1}>
            <Outlet />
          </main>
          {isPhone && <MobileNav />}
        </div>
      </div>

      {isPhone && (
        <D.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
          <D.Portal>
            <D.Overlay className={s.drawerOverlay} />
            <D.Content className={s.drawer} aria-describedby={undefined}>
              <D.Title className="df-visually-hidden">Main menu</D.Title>
              <Sidebar collapsed={false} onNavigate={() => setDrawerOpen(false)} />
            </D.Content>
          </D.Portal>
        </D.Root>
      )}

      {!isPhone && <ChatDock me={me} />}
      <ComposeDock isPhone={isPhone} />
      <Toaster />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} initialQuery={paletteQuery} onShowHelp={() => setHelpOpen(true)} onToggleSidebar={toggleSidebar} />
      <Cheatsheet open={helpOpen} onOpenChange={setHelpOpen} />
    </TooltipProvider>
  );
}
